import React from 'react';
import PropTypes from 'prop-types';
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import {observer} from "mobx-react";
import {computed} from "mobx";
import Typography from "@material-ui/core/Typography";


const styles = theme => ({
  indexName: {
    zIndex: 3,
  },
  columnName: {
    zIndex: 3,
  },
  leftBorder: {
    borderLeft: `1px solid ${theme.palette.grey[300]}`,
  },
  rightBorder: {
    borderRight: `1px solid ${theme.palette.grey[300]}`,
  },
});


@withStyles(styles)
@observer
class DataFrame extends React.Component {
  static propTypes = {
    serializedDataFrame: PropTypes.shape().isRequired,
    limit: PropTypes.number,
    maxHeight: PropTypes.number,
    filterColumn: PropTypes.func,
    filterRow: PropTypes.func,
    formatColumnName: PropTypes.func,
    formatIndexName: PropTypes.func,
    formatColumn: PropTypes.func,
    formatData: PropTypes.func,
    getColumnProps: PropTypes.func,
    getDataProps: PropTypes.func,
    TableContainerProps: PropTypes.shape(),
    TableProps: PropTypes.shape(),
  };
  static defaultProps = {
    limit: null,
    maxHeight: null,
    filterColumn: (serializedDataFrame, columnName, columnIndex) => true,
    filterRow: (serializedDataFrame, rowIndex) => true,
    formatColumnName: (serializedDataFrame, columnName, columnIndex) => columnName,
    formatIndexName: (serializedDataFrame, indexName, indexIndex) => indexName,
    formatColumn: (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => columnName,
    formatData: (serializedDataFrame, columnIndex, rowIndex, value) => value,
    getColumnProps: (serializedDataFrame, columnsRowIndex, columnName, columnIndex) => ({}),
    getDataProps: (serializedDataFrame, columnIndex, rowIndex, value) => ({}),
    TableContainerProps: {},
    TableProps: {},
  };

  addBorders = props => {
    const { classes } = this.props;
    if (props.leftBorder) {
      let className = classes.leftBorder;
      if ('className' in props) {
        className = `${className} ${props.className}`;
      }
      props.className = className;
    }
    if (props.rightBorder) {
      let className = classes.rightBorder;
      if ('className' in props) {
        className = `${className} ${props.className}`;
      }
      props.className = className;
    }
    delete props.leftBorder;
    delete props.rightBorder;
  };

  renderColumnNameCell = (columnName, columnIndex) => {
    const { serializedDataFrame, formatColumnName, classes } = this.props;
    return (
      <TableCell
        key={columnIndex}
        className={`${classes.columnName} ${classes.rightBorder}`}
        align="right"
        colSpan={serializedDataFrame.index_names.length}
      >
        {formatColumnName(serializedDataFrame, columnName, columnIndex)}
      </TableCell>
    );
  };

  renderIndexNameCell = (indexName, indexIndex) => {
    const { serializedDataFrame, formatIndexName, classes } = this.props;
    const classNames = [classes.indexName];
    if (indexIndex === serializedDataFrame.index_names.length - 1) {
      classNames.push(classes.rightBorder);
    }
    return (
      <TableCell key={indexIndex} className={classNames.join(' ')}>
        {formatIndexName(serializedDataFrame, indexName, indexIndex)}
      </TableCell>
    );
  };

  renderColumnCell = (columnsRowIndex, columnName, columnIndex, props) => {
    const { serializedDataFrame, formatColumn, getColumnProps } = this.props;
    props = { ...props, ...getColumnProps(serializedDataFrame, columnsRowIndex, columnName, columnIndex) };
    this.addBorders(props);
    return (
      <TableCell key={columnIndex} {...props}>
        {formatColumn(serializedDataFrame, columnsRowIndex, columnName, columnIndex)}
      </TableCell>
    );
  };

  renderIndexCell = (indexName, indexIndex, rowIndex, value) => {
    const { serializedDataFrame, filterRow, classes } = this.props;
    if (!filterRow(serializedDataFrame, rowIndex)) {
      return null;
    }
    return (
      <TableCell
        key={indexIndex}
        variant="head"
        className={indexIndex === serializedDataFrame.index_names.length - 1 ? classes.rightBorder : null}
      >
        <Typography noWrap>{value}</Typography>
      </TableCell>
    );
  };

  renderDataCell = (columnIndex, rowIndex, value) => {
    const { serializedDataFrame, filterColumn, filterRow, formatData, getDataProps } = this.props;
    const { columns } = serializedDataFrame;
    const columnName = columns[columnIndex];
    if (
      !filterColumn(serializedDataFrame, columnName, columnIndex)
      || !filterRow(serializedDataFrame, rowIndex)
    ) {
      return null;
    }
    const props = {...getDataProps(serializedDataFrame, columnIndex, rowIndex, value)};
    this.addBorders(props);
    return (
      <TableCell key={columnIndex} {...props}>
        {formatData(serializedDataFrame, columnIndex, rowIndex, value)}
      </TableCell>
    );
  };

  renderRow = (row, rowIndex) => {
    const { serializedDataFrame, filterRow } = this.props;
    if (!filterRow(serializedDataFrame, rowIndex)) {
      return null;
    }
    const { index, index_names } = serializedDataFrame;
    return (
      <TableRow key={rowIndex}>
        {index_names.map((indexName, indexIndex) =>
          this.renderIndexCell(indexName, indexIndex, rowIndex, index[rowIndex][indexIndex])
        )}
        {row.map((value, columnIndex) => this.renderDataCell(columnIndex, rowIndex, value))}
      </TableRow>
    );
  };

  renderLimitRow() {
    const { serializedDataFrame, limit } = this.props;
    const { columns, index_names } = serializedDataFrame;
    const rowIndex = limit + 1;
    return (
      <TableRow>
        {index_names.map((indexName, indexIndex) => this.renderIndexCell(indexName, indexIndex, rowIndex, '…'))}
        {columns.map((column, columnIndex) => this.renderDataCell(columnIndex, rowIndex, '…'))}
      </TableRow>
    );
  }

  @computed get columnsRows() {
    const { serializedDataFrame, filterColumn } = this.props;
    const { columns_names, columns } = serializedDataFrame;
    return columns_names.map((columnsName, columnNameIndex) => {
      const columnsRow = [];
      let column = null;
      for (let index = 0; index < columns.length; index++) {
        const name = columns[index][columnNameIndex];
        if (!filterColumn(serializedDataFrame, columns[index], index)) {
          continue;
        }
        if (column !== null && name === column.name) {
          column.colSpan += 1;
        } else {
          column = { index, name, colSpan: 1};
          columnsRow.push(column);
        }
      }
      return { columnsName, columnsRow };
    });
  }

  render() {
    const { serializedDataFrame, limit, maxHeight, TableContainerProps, TableProps } = this.props;
    let { data, index_names } = serializedDataFrame;
    const isLimited = limit !== null && data.length > limit;
    if (isLimited) {
      data = data.slice(0, limit);
    }
    const { columnsRows } = this;
    return (
      <TableContainer style={{ maxHeight }} component={Paper} {...TableContainerProps}>
        <Table stickyHeader size="small" {...TableProps}>
          <TableHead>
            {columnsRows.map(({ columnsName, columnsRow }, columnsRowIndex) =>
              <TableRow key={columnsRowIndex}>
                {
                  columnsRowIndex === columnsRows.length - 1
                    ? index_names.map(this.renderIndexNameCell)
                    : this.renderColumnNameCell(columnsName, columnsRowIndex)
                }
                {columnsRow.map(column =>
                  this.renderColumnCell(columnsRowIndex, column.name, column.index, { colSpan: column.colSpan })
                )}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {data.map(this.renderRow)}
            {isLimited && this.renderLimitRow()}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
}


export default DataFrame;
